import useLocales from "@hooks/useLocales";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { DeleteAsync, GetAsync, PostAsync } from "src/common/httpRequests";
import { MANUAL_DOCUMENT_SERVICE_URL } from "src/constants/api";
import { API_GATEWAY } from "src/constants/settings";

export interface IDocumentSeries {
  id: string;
  series: string;
  initialSequenceNumber: number;
  createdOn: string;
  updatedOn?: string;
  isActive: boolean;
}

const mapDocumentSeries = (item: any): IDocumentSeries => ({
  ...item,
  id: item.id ?? item.Id ?? item.documentNoSeriesId ?? item.DocumentNoSeriesId,
  series: item.series ?? item.Series,
  initialSequenceNumber:
    item.initialSequenceNumber ?? item.InitialSequenceNumber,
  createdOn: item.createdOn ?? item.CreatedOn,
  updatedOn: item.updatedOn ?? item.UpdatedOn,
  isActive: item.isActive === true,
});

export const useDocumentSeries = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales("documents");
  const [data, setData] = useState<IDocumentSeries[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Search
  const searchDocumentNoSeries = (
    documentClass: number = 0,
    complianceStandardDocumentType: number = 0,
    pageNumber: number = 1,
    pageSize: number = 5,
  ) => {
    setIsLoading(true);

    const queryParams = new URLSearchParams({
      accountClass: "0",
      documentClass: documentClass.toString(),
      complianceStandardDocumentType: complianceStandardDocumentType.toString(),
      page: pageNumber.toString(),
      record: pageSize.toString(),
      ignorePagecount: "true",
    }).toString();

    GetAsync(
      `${API_GATEWAY}${MANUAL_DOCUMENT_SERVICE_URL}/SearchDocumentNoSeries?${queryParams}`,
    )
      .then((res: any) => {
        if (res.status === 200) {
          const payload = res.data || {};
          const arrayData =
            [
              payload,
              payload.items,
              payload.documentNoSeriesDetails,
              payload.data,
            ].find(Array.isArray) || [];

          setData(arrayData.map(mapDocumentSeries));
          const count =
            payload.totalCount || payload.totalRecords || payload.count || 0;
          setTotalCount(count);
        } else {
          if (res?.data?.errors) {
            Object.values(res.data.errors).forEach((item: any) => {
              if (Array.isArray(item)) {
                item.forEach((error: string) => {
                  enqueueSnackbar(error, { variant: "error" });
                });
              }
            });
          } else {
            enqueueSnackbar(
              translate(res?.data?.Detail || "Error fetching data"),
              { variant: "error" },
            );
          }
        }
      })
      .catch((err) => {
        console.log("Error fetching document series", err);
        enqueueSnackbar(translate("something_went_wrong"), {
          variant: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Create
  const createDocumentNoSeries = (
    series: string,
    initialNum: number,
    documentClass: number = 0,
    complianceStandardDocumentType: number = 0,
  ) => {
    setIsLoading(true);
    const payload = {
      documentNoSeries: {
        series: series,
        initialSequenceNumber: initialNum,
        accountClass: 0,
        documentClass: documentClass,
        complianceStandardDocumentType: complianceStandardDocumentType,
      },
    };

    return PostAsync(
      `${API_GATEWAY}${MANUAL_DOCUMENT_SERVICE_URL}/CreateDocumentNoSeries`,
      payload,
    )
      .then((res: any) => {
        if (res.status === 200) {
          enqueueSnackbar(translate("Record created successfully"), {
            variant: "success",
          });
          searchDocumentNoSeries(documentClass, complianceStandardDocumentType);

          return true;
        } else {
          if (res?.data?.errors) {
            Object.values(res.data.errors).map((item: any) => {
              if (Array.isArray(item)) {
                item.map((error: string) => {
                  enqueueSnackbar(error, { variant: "error" });
                });
              }
            });
          } else {
            enqueueSnackbar(
              translate(res?.data?.Detail || "Error creating record"),
              { variant: "error" },
            );
          }
          setIsLoading(false);

          return false;
        }
      })
      .catch((err) => {
        console.log("Error creating document series", err);
        enqueueSnackbar(translate("something_went_wrong"), {
          variant: "error",
        });
        setIsLoading(false);
        return false;
      });
  };

  // Delete
  const deleteDocumentNoSeries = (
    id: string,
    documentClass: number = 0,
    complianceStandardDocumentType: number = 0,
  ) => {
    setIsLoading(true);

    return DeleteAsync(
      `${API_GATEWAY}${MANUAL_DOCUMENT_SERVICE_URL}/DeleteDocumentNoSeries/${id}`,
    )
      .then((res: any) => {
        if (res.status === 200) {
          searchDocumentNoSeries(documentClass, complianceStandardDocumentType);
          setIsLoading(false);
          return true;
        } else {
          if (res?.data?.errors) {
            Object.values(res.data.errors).map((item: any) => {
              if (Array.isArray(item)) {
                item.map((error: string) => {
                  enqueueSnackbar(error, { variant: "error" });
                });
              }
            });
          } else {
            enqueueSnackbar(
              translate(res?.data?.Detail || "Error deleting record"),
              { variant: "error" },
            );
          }
          setIsLoading(false);
          return false;
        }
      })
      .catch((err) => {
        console.log("Error deleting document series", err);
        enqueueSnackbar(translate("something_went_wrong"), {
          variant: "error",
        });
        setIsLoading(false);
        return false;
      });
  };

  const generateDocumentNumberWithSeries = async (series: string) => {
    try {
      const response = await axios.get(
        `${API_GATEWAY}${MANUAL_DOCUMENT_SERVICE_URL}/GenerateDocumentNumberWithSeries?accountClass=0&documentType=0&complianceStandardDocumentType=0/${series}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error generate number:", error);
      return null;
    }
  };

  return {
    data,
    isLoading,
    totalCount,
    searchDocumentNoSeries,
    createDocumentNoSeries,
    deleteDocumentNoSeries,
    generateDocumentNumberWithSeries,
  };
};
